import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isEditableElement } from './viewportFrame';
import {
  predictKeyboardTop, INLINE_REVEAL_SAFETY_PX, isGuardedSurfacePath, keyboardKindFor,
} from '../../utils/ui/inlineRevealPlan';
import { canPredictKeyboard, installUserGestureFocusTracking, isUserGestureFocus } from '../../utils/ui/keyboardPresence';
import { KB_MOTION, dialogFitsAbovePredictedKeyboard, glideTopFloorPx } from '../../components/common/keyboardOverlayStyles';
import { kbLog } from '../../utils/ui/keyboardReframeDebug';
import { flipSlide, probePan } from './modalAnticipationSlide';
import { EXEMPT_SELECTOR } from '../../utils/ui/inlineKeyboardGuard';
import { createStepTuck } from './modalStepTuck';
import { createContentFlip } from './modalContentFlip';
import { createHeightFreeze, bodyScrollerOverflows, findBodyScroller } from './modalHeightFreeze';
import {
  DISMISS_SUPPRESS_MS, releaseOurSpacer, captureScrollLoan,
} from './modalCloseChoreography';
import { createBodyShrink, realKeyboardTop, realKeyboardKnown } from './modalBodyShrink';
import { createTapRest } from './modalTapRest';
import useModalCoordinatedClose from './useModalCoordinatedClose';

/** KAN-1181: the panel is in a tap-rest typing session (set by the shell and, at the tap, by modalTapRest). */
const isRestPanel = (el) => !!(el && el.hasAttribute && el.hasAttribute('data-cvx-kb-rest'));

/**
 * DYNAMIC dialog anticipation — the modal-side half of iOS pan prevention
 * (v2, 2026-08-21; supersedes both the one-shot v1 AND the static
 * band-centered scrim it briefly shipped alongside).
 *
 * THE LAW (see `inlineRevealPlan.js`): iOS pans the whole viewport so the
 * focused field's centre lands at `visibleHeight / 2`, sampled right after
 * the focus handlers run. A field pinned in a modal with no usable scroller
 * can only be protected by MOVING THE DIALOG above the centring line before
 * iOS samples. But a dialog that PARKS there while the keyboard is down
 * reads as mis-centered (founder PWA report: "why is it not centered?") —
 * so the anchor is a LIFECYCLE, not a position:
 *
 *   rest (centered) ──focus──▶ ANCHORED (instant, pre-sample)
 *        ▲                          │ keyboard gone ≥ CLOSE_START_MS
 *        └──── animated glide ◀─────┘ (padding-top on the keyboard curve)
 *
 * This is exactly how a native iOS alert with a text field behaves: it sits
 * centered, rides above the keyboard while it's up, and settles back.
 *
 * KAN-1181: a tap on a typing field of an eligible touch card runs the SAME
 * anchor lifecycle to the header floor, but as a REST card (`modalTapRest`:
 * body shrink instead of freeze + lift); the close glides it back to centre.
 * Mobile modals never auto-summon the keyboard (see the blur at the bottom).
 *
 * Self-coordinating with the inline guard by ORDER + GEOMETRY: this hook's
 * WINDOW-capture focusin runs BEFORE the guard's document-capture listener
 * (it pins the height and lifts first, so the guard measures the lifted
 * layout and scrolls only the remainder). A tall card whose body already
 * overflows and does not fit above the keyboard is left to the guard
 * (no lift); `needed ≤ 0` also stands the lift down. Exactly one actor moves
 * the card per focus.
 *
 * The anchored style is ALSO applied imperatively to the scrim node inside
 * the focus handler — the same synchronous-before-iOS-samples guarantee the
 * guard's scrollTop write relies on; the state mirror keeps React renders
 * consistent with it. Touch-only (coarse pointer): desktop has no on-screen
 * keyboard and keeps its centered card.
 *
 * @param {{ dialogEl: HTMLElement|null, keyboardOpen: boolean }} args
 * @returns {object|null} scrim style override, or null at rest
 */

// The close starts CLOSE_START_MS (140) after keyboardOpen flips false — the
// one-beat close (KAN-930) replaced the old 400ms RELEASE_LINGER_MS.

// Dismiss-suppression + release timing constants live in `modalCloseChoreography`.

// `canPredictKeyboard` is shared with `inlineKeyboardGuard` (desktop and a tablet
// with a hardware keyboard raise none) — see `keyboardPresence.js`.

// The retired dev-only `?kbslide` switch persisted its choice here; clear any
// leftover 'off' once so no device stays on the old instant jump.
try {
  if (typeof window !== 'undefined') window.localStorage.removeItem('cvx:kbslide');
} catch { /* storage unavailable — nothing to clear */ }

export default function useModalKeyboardAnticipation({
  dialogEl, keyboardOpen, enterMorphActive = false, visible = true, onRestFallback = null, suppressBridge = null,
  tapRest = false, onTapRest = null, restReleased = false,
}) {
  // Cancels a pending/running dialog FLIP + content flips (set by the layout effect).
  const cancelMotionRef = useRef(null);
  // null (rest) | { top } (anchored) | { top, releasing } (gliding back)
  const [anchor, setAnchor] = useState(null);
  const keyboardOpenRef = useRef(false);
  keyboardOpenRef.current = keyboardOpen;
  // Was the keyboard ever registered open since this lift began? (KAN-646)
  const keyboardSeenRef = useRef(false);
  if (keyboardOpen) keyboardSeenRef.current = true;
  const enterMorphRef = useRef(false);
  enterMorphRef.current = !!enterMorphActive;
  const anchorRef = useRef(null);
  anchorRef.current = anchor;
  const suppressUntilRef = useRef(0);
  const freezeRef = useRef(null);
  const tuckRef = useRef(null);
  // Scroll loan taken over from the inline guard: { scroller, originalScrollTop }.
  const loanRef = useRef(null);
  // The last user-focused editable inside the dialog (for scroller lookup).
  const fieldRef = useRef(null);
  // Backup clock that ends a coordinated close (spacer + scroll + freeze).
  const finishTimerRef = useRef(0);
  const closingRef = useRef(false);
  // KAN-930: rest cards shrink their BODY for the reveal (no spacer, no freeze).
  const shrinkRef = useRef(null);
  const [shrinkTick, setShrinkTick] = useState(0);
  // Rest card whose shrink FAILED this session → today's path (KAN-930 fallback).
  const restFallbackRef = useRef(false);
  const fallbackCbRef = useRef(onRestFallback);
  fallbackCbRef.current = onRestFallback;
  // KAN-1181: a tap on a typing field glides the card to the floor (modalTapRest).
  const tapRestOnRef = useRef(false);
  tapRestOnRef.current = tapRest;
  const tapRestedRef = useRef(false); // this typing session is a tap-rested one
  const onTapRestRef = useRef(onTapRest);
  onTapRestRef.current = onTapRest;
  const restReleasedRef = useRef(false);
  restReleasedRef.current = restReleased;
  // The in-flight close (abort / cancelBlur) + when the keyboard last closed.
  const closeCtlRef = useRef(null);
  const closedAtRef = useRef(0);
  // Cancel for the rAF scroll-back of the loan (null when idle).
  const cancelScrollRef = useRef(null);
  const stopScrollBack = useCallback(() => {
    if (cancelScrollRef.current) cancelScrollRef.current();
    cancelScrollRef.current = null;
  }, []);
  /** Stable — evaluated at CLICK time by the shell's scrim gate. */
  const isDismissSuppressed = useCallback(() => Date.now() < suppressUntilRef.current, []);
  // Pushes the dismiss gate forward by `ms`, never shortens it — armed by the rest release glide (useModalRestRelease).
  const suppressDismiss = useCallback((ms) => { suppressUntilRef.current = Math.max(suppressUntilRef.current, Date.now() + ms); }, []);
  if (suppressBridge) suppressBridge.current = suppressDismiss; // eslint-disable-line no-param-reassign

  useLayoutEffect(() => {
    if (!dialogEl) return undefined;

    let cancelFlip = null;
    let panTimer = null;

    const tryAnticipate = (el) => {
      // Never move the dialog while the keyboard is UP — glide/curtain own
      // its geometry then, measured from the current resting rect.
      if (keyboardOpenRef.current) return;
      // The enter morph owns the card's transform/geometry — never lift mid-morph.
      if (enterMorphRef.current) return;
      if (!isEditableElement(el)) return;
      if (!canPredictKeyboard()) return;
      // Geometry gate (KAN-552 1b): a TALL card (does not fit above the
      // keyboard) whose body scroller ALREADY overflows (builders — top at the
      // header floor) stays still; the guard reveals inside it. Every other
      // card (short, or tall-but-fits) slides up toward the header floor by
      // only as much as the field needs.
      const floor = glideTopFloorPx();
      if (bodyScrollerOverflows(dialogEl, el)
        && !dialogFitsAbovePredictedKeyboard(dialogEl.offsetHeight, window.innerHeight, floor, keyboardKindFor(el))) return;
      const kbTop = predictKeyboardTop(window.innerHeight, keyboardKindFor(el));
      const r = el.getBoundingClientRect();
      const dialogTop = dialogEl.getBoundingClientRect().top;
      const needed = ((r.top + r.bottom) / 2) - (kbTop / 2) + INLINE_REVEAL_SAFETY_PX;
      if (needed <= 0) return; // already above the line (incl. guard-revealed)

      // Client rects on purpose (not offsets): during the enter slide both
      // the dialog and the field are translated by the same amount, so the
      // shift cancels out of `restingTop − needed`.
      const restingTop = dialogTop;
      const newTop = Math.max(floor, Math.round(restingTop - needed));
      if (newTop >= restingTop) return; // at the floor — nothing to gain

      const scrimEl = dialogEl.parentElement;
      if (!scrimEl) return;
      // Imperative-first: anchored BEFORE iOS samples, whatever React's
      // render timing; `transition: none` also cancels an in-flight release
      // glide when the user re-taps mid-return.
      scrimEl.style.transition = 'none';
      scrimEl.style.alignItems = 'flex-start';
      scrimEl.style.paddingTop = `${newTop}px`;
      suppressUntilRef.current = Date.now() + DISMISS_SUPPRESS_MS;
      kbLog('anticipate', { from: Math.round(restingTop), to: newTop, kbTop });
      // The paint SLIDES while layout is already final (deferred FLIP — always
      // on; the dev-only `?kbslide` switch was removed 2026-10-01).
      if (cancelFlip) cancelFlip();
      cancelFlip = flipSlide(dialogEl, restingTop - newTop);
      if (panTimer) clearTimeout(panTimer);
      panTimer = probePan('flip');
      // restingTop/restingH ride in the anchor so the release glides back to
      // exactly where the card rests (centered in the header/pill band). When
      // an anchor already exists (re-tap during the release linger or glide)
      // the card is lifted / mid-glide right now, so the ORIGINAL resting
      // values carry over instead of being re-measured.
      const existing = anchorRef.current;
      if (!existing) keyboardSeenRef.current = keyboardOpenRef.current;
      const next = {
        top: newTop,
        restingTop: existing?.restingTop ?? Math.round(restingTop),
        restingH: existing?.restingH ?? dialogEl.offsetHeight,
      };
      anchorRef.current = next;
      setAnchor(next);
    };

    const stopGesture = installUserGestureFocusTracking();

    // Window-capture focusin (runs before the guard's document capture):
    // (a) RD-DEV-020: tall curtain-bound cards that FIT their content get their
    // height pinned at the tap, BEFORE the inline guard's document-capture
    // focusin runs (window capture precedes document capture), so the guard
    // can reveal inside a now-fixed body instead of iOS panning the app;
    // (b) the anticipation lift (KAN-552 1b).
    // Rotation: the freeze already released its pin; the lift was measured for
    // the old geometry, so drop it too (scrim styles are imperative).
    const tuck = createStepTuck(dialogEl, {
      isKeyboardOpen: () => keyboardOpenRef.current,
      isPinned: () => freezeRef.current != null && freezeRef.current.isFrozen(),
      onRotate: () => onRotate(),
    });
    tuckRef.current = tuck;
    const onRotate = () => {
      tuck.cancel();
      tapRestApi.cancel(); // eslint-disable-line no-use-before-define
      if (shrinkRef.current) shrinkRef.current.finish(null);
      if (cancelFlip) { cancelFlip(); cancelFlip = null; }
      const scrimEl = dialogEl.parentElement;
      if (scrimEl) {
        scrimEl.style.removeProperty('transition');
        scrimEl.style.removeProperty('align-items');
        scrimEl.style.removeProperty('padding-top');
      }
      anchorRef.current = null;
      setAnchor(null);
    };
    const tapRestApi = createTapRest(dialogEl, {
      isEnabled: () => tapRestOnRef.current && !keyboardOpenRef.current,
      isMorphing: () => enterMorphRef.current,
      anchorRef, setAnchor, keyboardSeenRef, keyboardOpenRef, suppressUntilRef,
      onTapRest: (on) => { tapRestedRef.current = on; if (onTapRestRef.current) onTapRestRef.current(on); },
    });
    const freeze = createHeightFreeze(dialogEl, { onRotate });
    const shrink = createBodyShrink(dialogEl, { onRotate: () => onRotate() });
    shrinkRef.current = shrink;
    const contentFlip = createContentFlip(dialogEl);
    contentFlip.attach();
    cancelMotionRef.current = () => {
      if (cancelFlip) { cancelFlip(); cancelFlip = null; }
      tapRestApi.cancel();
      contentFlip.cancel();
    };
    freezeRef.current = freeze;
    const onWindowFocusIn = (e) => {
      if (!(e.target instanceof Node) || !dialogEl.contains(e.target) || !isEditableElement(e.target)) return;
      fieldRef.current = e.target;
      let rest = isRestPanel(dialogEl);
      if (keyboardOpenRef.current) {
        // Field-to-field tap while the keyboard is up: the guard's reveal scroll still glides.
        if (isUserGestureFocus(e) && canPredictKeyboard()) {
          // Rest card: plan the shrink against the guard's target + the REAL band
          // (a fresh start when this session had none yet; never in fallback).
          if (rest && !restFallbackRef.current) {
            shrink.shrinkFor(e.target, { kbTop: predictKeyboardTop(window.innerHeight, keyboardKindFor(e.target)), kbLine: realKeyboardTop() });
          }
          contentFlip.cancel(); contentFlip.record(e.target, { startAfterStall: rest });
        }
        return;
      }
      if (!isUserGestureFocus(e) || !canPredictKeyboard()) return;
      // A re-tap during the coordinated close cancels it: stop the scroll-back
      // and the height tween at the current geometry (the re-anchor below, and
      // the guard's own reveal, take it from there).
      if (closingRef.current) {
        closingRef.current = false;
        stopScrollBack(); // leaves scrollTop where the animation had got to
        if (closeCtlRef.current) closeCtlRef.current.cancelBlur();
        freeze.cancelRelease();
        shrink.cancelRelease();
        clearTimeout(finishTimerRef.current);
      }
      // KAN-1181: the tap itself glides the card to the floor (synchronously, here)
      // and makes it a rest card for this session — ONE mover, so no 1b lift below.
      if (tapRestApi.maybeEngage(e.target, rest)) rest = true;
      // KAN-930 rest card: shrink the body instead of freeze + spacer + lift.
      // 'failed' (range unobtainable) → today's path below, unchanged.
      const guarded = isGuardedSurfacePath(window.location.pathname)
        && !(typeof e.target.closest === 'function' && e.target.closest(EXEMPT_SELECTOR));
      const shrunk = rest && guarded
        ? shrink.shrinkFor(e.target, { kbTop: predictKeyboardTop(window.innerHeight, keyboardKindFor(e.target)) })
        : 'failed';
      if (rest) {
        restFallbackRef.current = shrunk === 'failed';
        if (fallbackCbRef.current) fallbackCbRef.current(shrunk === 'failed');
      }
      // The opening shrink moves the card: a scrim tap mid-motion never dismisses.
      if (shrunk === 'shrunk') suppressUntilRef.current = Date.now() + DISMISS_SUPPRESS_MS;
      if (shrunk !== 'failed') {
        if (!anchorRef.current) keyboardSeenRef.current = keyboardOpenRef.current; // KAN-646 per session
        setShrinkTick((n) => n + 1);
        contentFlip.cancel();
        contentFlip.record(e.target, { startAfterStall: true });
        return;
      }
      // Freeze: same skip rules as the guard (path / EXEMPT / fits) — these
      // gates apply to the freeze ONLY, never to the lift below.
      const frozen = isGuardedSurfacePath(window.location.pathname)
        && !(typeof e.target.closest === 'function' && e.target.closest(EXEMPT_SELECTOR))
        && !dialogFitsAbovePredictedKeyboard(
          dialogEl.offsetHeight, window.innerHeight, glideTopFloorPx(), keyboardKindFor(e.target),
        );
      // Pop-ups that do not opt in (no marked bar) pay nothing below.
      const hasBar = !!dialogEl.querySelector('[data-cvx-kb-tuck]');
      const scroller = hasBar ? findBodyScroller(dialogEl, e.target) : null;
      if (frozen) {
        // A re-tap mid-unfold: finish the tuck's unfold and drop ITS pin first
        // (positions are captured before the snap), or the freeze would take
        // the tuck's stale px height as "original".
        if (hasBar) tuck.settle(scroller);
        // Still tucked and holding the pin: hand its prior inline values over.
        const handed = hasBar ? tuck.takePin() : null;
        if (!freeze.freeze(e.target, handed) && handed) tuck.giveBackPin(handed);
      }
      // Step-bar tuck (KAN-552 1e): BEFORE the lift, so `needed` is measured
      // after the freed space. Never on a body that already scrolls (builders);
      // `tuck` itself requires a body scroller to glide.
      if (hasBar && !tuck.isTucked() && !bodyScrollerOverflows(dialogEl, e.target)) {
        tuck.tuck(scroller);
      }
      // Lift BEFORE the guard's document-capture handler runs, so the guard
      // measures the lifted layout and scrolls only the remainder.
      tryAnticipate(e.target);
      // Content glide: remember the scroller's scrollTop; the dialog-level
      // listener (after the guard) FLIPs any reveal scroll.
      contentFlip.cancel();
      contentFlip.record(e.target);
      // The re-tap may not re-anchor (`needed <= 0`): the anchor would still be
      // `releasing` with its timers cancelled and the card stranded mid-glide.
      // Make it a NORMAL anchor at the card's CURRENT position so the next
      // keyboard close runs the whole choreography again.
      const a = anchorRef.current;
      const scrimEl = dialogEl.parentElement;
      if (a && a.releasing != null && scrimEl) {
        const top = Math.round(parseFloat(window.getComputedStyle(scrimEl).paddingTop) || a.releasing);
        scrimEl.style.transition = 'none';
        scrimEl.style.alignItems = 'flex-start';
        scrimEl.style.paddingTop = `${top}px`;
        const held = { ...a, top, releasing: undefined };
        anchorRef.current = held;
        setAnchor(held);
      }
    };
    window.addEventListener('focusin', onWindowFocusIn, true);
    // Focus leaving the dialog ends the typing session: release (the freeze
    // still waits for the guard's spacer to drop).
    const onFocusOut = (e) => {
      const next = e.relatedTarget;
      if (next instanceof Node && dialogEl.contains(next)) return;
      // A lifted card / held loan is released by the coordinated close instead.
      if (anchorRef.current || loanRef.current) return;
      if (!keyboardOpenRef.current) tuck.untuck({ animate: true });
      freeze.onKeyboardClose();
    };
    dialogEl.addEventListener('focusout', onFocusOut);
    // MOBILE MODALS NEVER AUTO-SUMMON THE KEYBOARD (native iOS convention; in the
    // installed PWA autoFocus left an anchored card next to NO keyboard, 2026-08-21).
    // autoFocus fires in the commit mutation phase; this layout effect runs
    // pre-paint, so blurring here keeps the first frame the CENTERED card. The
    // user's tap summons the keyboard and the focusin listener moves the card.
    // Desktop and a tablet with a hardware keyboard keep autoFocus (no keyboard).
    const active = document.activeElement;
    if (canPredictKeyboard() && dialogEl.contains(active) && isEditableElement(active)) {
      active.blur();
    }
    // Reads the ref at cleanup time on purpose: it holds the LATEST close
    // timer id (set long after this effect ran), never a DOM node.
    const clearFinishTimer = () => clearTimeout(finishTimerRef.current);
    return () => {
      stopGesture();
      window.removeEventListener('focusin', onWindowFocusIn, true);
      dialogEl.removeEventListener('focusout', onFocusOut);
      tuck.dispose();
      tapRestApi.cancel();
      tuckRef.current = null;
      shrink.dispose();
      shrinkRef.current = null;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- the LATEST close controller, read at cleanup on purpose
      if (closeCtlRef.current) closeCtlRef.current.cancelBlur();
      freeze.dispose();
      contentFlip.dispose();
      cancelMotionRef.current = null;
      freezeRef.current = null;
      clearFinishTimer();
      stopScrollBack();
      releaseOurSpacer(loanRef.current);
      loanRef.current = null;
      if (cancelFlip) cancelFlip();
      if (panTimer) clearTimeout(panTimer);
    };
  }, [dialogEl, stopScrollBack]);

  // Closing (visible → false): a pending flip must not race the exit transform.
  useEffect(() => {
    if (!visible && cancelMotionRef.current) cancelMotionRef.current();
  }, [visible]);

  // The coordinated close handed the card back (anchor null): the tap-rest session is over.
  useEffect(() => {
    if (!anchor && tapRestedRef.current) { tapRestedRef.current = false; if (onTapRestRef.current) onTapRestRef.current(false); }
  }, [anchor]);

  // Height freeze follows the measured keyboard: open → keep; close → release
  // once the guard has dropped its spacer. (Initial mount: nothing frozen.)
  // When this dialog was lifted and/or frozen, the guard's scroll loan for the
  // field's body scroller is taken over here and its spacer HELD, so closing
  // is ONE coordinated move (see `run` below) instead of three.
  const captureLoan = useCallback(() => captureScrollLoan({
    dialogEl, freeze: freezeRef.current, fieldRef, anchorRef, loanRef, shrink: shrinkRef.current,
  }), [dialogEl]);

  useEffect(() => {
    const f = freezeRef.current;
    if (!f) return;
    if (keyboardOpen) {
      // Back before the close finished (KAN-561): no blur, typing geometry back.
      if (closeCtlRef.current) closeCtlRef.current.abort();
      f.onKeyboardOpen();
      if (tuckRef.current) tuckRef.current.onKeyboardOpen();
      // Rest card: the real keyboard may be taller than predicted.
      if (shrinkRef.current && shrinkRef.current.isActive()) shrinkRef.current.settleTo(realKeyboardTop(), { grow: realKeyboardKnown() });
      captureLoan();
    } else {
      closedAtRef.current = Date.now();
      if (!anchorRef.current && !loanRef.current) f.onKeyboardClose();
    }
  }, [keyboardOpen, dialogEl, captureLoan]);

  // COORDINATED CLOSE (KAN-552 1c; one beat since KAN-930) — see useModalCoordinatedClose.
  useModalCoordinatedClose({
    dialogEl, keyboardOpen, anchor, setAnchor, shrinkTick, restReleased, captureLoan, stopScrollBack,
    refs: {
      freezeRef, tuckRef, shrinkRef, loanRef, closingRef, finishTimerRef, cancelScrollRef,
      suppressUntilRef, keyboardSeenRef, closedAtRef, closeCtlRef, tapRestedRef,
    },
  });

  if (!anchor) return { scrimStyle: null, isDismissSuppressed };
  return anchor.releasing != null
    ? {
      isDismissSuppressed,
      scrimStyle: {
        alignItems: 'flex-start',
        paddingTop: `${anchor.releasing}px`,
        transition: `padding-top ${KB_MOTION}`,
      },
    }
    : {
      isDismissSuppressed,
      // `returning`: the keyboard came back mid-close — glide up, never snap.
      scrimStyle: { alignItems: 'flex-start', paddingTop: `${anchor.top}px`, transition: anchor.returning ? `padding-top ${KB_MOTION}` : 'none' },
    };
}
